import { BaseRepository } from '@/base/base-repository';
import { getCurrentAuthToken } from '@/hooks/auth/utils/auth-token-registry';
import type { SelfExclusionStatus, SocialProvider, UserProfileDto } from '@/types/social';

class ProfileRepository extends BaseRepository {
	async getProfile(address: string): Promise<UserProfileDto> {
		const token = getCurrentAuthToken();
		const res = await this.httpClient({
			method: 'post',
			url: `/users/profile`,
			data: { address },
			headers: token ? { Authorization: `Bearer ${token}` } : undefined,
		});
		return res;
	}

	async disconnectSocial(provider: SocialProvider, address: string) {
		const token = getCurrentAuthToken();
		return this.httpClient({
			method: 'delete',
			url: `/social/disconnect`,
			data: { provider, address },
			headers: token ? { Authorization: `Bearer ${token}` } : undefined,
		});
	}

	async updateProfile(address: string, payload: { email?: string | null; communicationOptIn?: boolean }): Promise<UserProfileDto> {
		const token = getCurrentAuthToken();
		return this.httpClient({
			method: 'patch',
			url: `/users/profile`,
			data: { address, ...payload },
			headers: token ? { Authorization: `Bearer ${token}` } : undefined,
		});
	}

	async setSelfExclusion(address: string, durationDays: number): Promise<SelfExclusionStatus> {
		const token = getCurrentAuthToken();
		return this.httpClient({
			method: 'post',
			url: `/users/self-exclusion`,
			data: { address, durationDays },
			headers: token ? { Authorization: `Bearer ${token}` } : undefined,
		});
	}

	async verifyEmailToken(token: string): Promise<void> {
		return this.httpClient({ method: 'post', url: `/users/email/verify`, data: { token }, raw: true });
	}

	async resendVerificationEmail(address: string): Promise<void> {
		const token = getCurrentAuthToken();
		return this.httpClient({
			method: 'post',
			url: `/users/email/resend`,
			data: { address },
			headers: token ? { Authorization: `Bearer ${token}` } : undefined,
		});
	}

	async getSocialOAuthUrl(provider: SocialProvider, address: string, redirectUrl?: string | null): Promise<{ url: string }> {
		const token = getCurrentAuthToken();
		return this.httpClient({
			method: 'post',
			url: `/social/oauth-url`,
			data: { provider, address, redirectUrl },
			headers: token ? { Authorization: `Bearer ${token}` } : undefined,
		});
	}

	async completeSocialCallback(
		provider: SocialProvider,
		payload: Record<string, string>,
	): Promise<{ provider: SocialProvider; address: string; username: string; avatarUrl: string | null }> {
		const token = getCurrentAuthToken();
		return this.httpClient({
			method: 'post',
			url: `/social/callback`,
			data: { provider, ...payload },
			headers: token ? { Authorization: `Bearer ${token}` } : undefined,
		});
	}
}

export const profileRepository = new ProfileRepository();
